import { useEffect, useRef, type ReactNode } from "react";

interface OverflowMenuProps {
	isOpen: boolean;
	onClose: () => void;
	children: ReactNode;
}

/**
 * A dropdown menu that appears below the trigger button.
 * Used in the mobile header for collapsed controls.
 * Closes on outside click or Escape.
 */
export function OverflowMenu({ isOpen, onClose, children }: OverflowMenuProps) {
	const menuRef = useRef<HTMLDivElement>(null);

	// Close on outside click
	useEffect(() => {
		if (!isOpen) return;
		function handleClick(e: MouseEvent) {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				onClose();
			}
		}
		// Delay adding listener so the opening click doesn't immediately close
		const timer = setTimeout(() => {
			document.addEventListener("mousedown", handleClick);
		}, 0);
		return () => {
			clearTimeout(timer);
			document.removeEventListener("mousedown", handleClick);
		};
	}, [isOpen, onClose]);

	// Close on Escape
	useEffect(() => {
		if (!isOpen) return;
		function handleKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div
			ref={menuRef}
			className="absolute right-0 top-full mt-1 w-72 bg-bg-secondary border border-border-default rounded-lg shadow-lg z-50 p-3 space-y-3"
		>
			{children}
		</div>
	);
}
